/**
 * DonorEventsPage — donor events console (galas / dinners / webinars).
 *
 * Phase 3 turned this from a stack of modals into pages. Creating an event was a
 * `max-w-md` modal, and the whole event — deck, giving, invitations, roster,
 * check-in — was crammed into one `max-w-lg` modal that opened the card reader as
 * a modal on top of a modal. Now: a list, a full-page form, and a full-surface
 * detail view with sub-tabs, each deep-linkable via `?event=<id>&view=<view>` so
 * a refresh or a shared link lands where you were.
 *
 * The authenticated shell is tab-state driven rather than router-driven, so the
 * URL is synced with history.replaceState — the same approach other deep-linked
 * surfaces here use.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  Calendar,
  CheckSquare,
  DollarSign,
  Link2,
  MapPin,
  Pencil,
  Plus,
  Presentation,
  RefreshCw,
  Send,
  Target,
  Trash2,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTabbedChrome } from '@/components/weavein';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DonorEvent,
  DonorEventCreate,
  createDonorEvent,
  deleteDonorEvent,
  eventGuestsToPipeline,
  getDonorEvent,
  listDonorEvents,
  updateDonorEvent,
} from '@/services/api/donorEvents';
import { Donation, listDonations } from '@/services/api/donations';
import { EventPresentation } from '@/components/donors/EventPresentation';
import DonorEventForm from '@/components/donors/DonorEventForm';
import EventInvitations from '@/components/donors/EventInvitations';
import EventCheckIn from '@/components/donors/EventCheckIn';
import EventGivingStation from '@/components/donors/EventGivingStation';

type View = 'list' | 'new' | 'edit' | 'detail';
type DetailTab = 'overview' | 'invitations' | 'guests' | 'giving' | 'presentation';

function fmtMoney(n?: number | null): string {
  return `$${(n ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function fmtRange(start?: string | null, end?: string | null): string {
  if (!start) return '';
  const s = new Date(start);
  const base = s.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  if (end) return `${base} – ${new Date(end).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  return base;
}

const RSVP_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  accepted: 'secondary',
  declined: 'destructive',
  tentative: 'default',
  pending: 'outline',
};

const DETAIL_TABS: { id: DetailTab; label: string; icon: React.ElementType }[] = [
  { id: 'overview', label: 'Overview', icon: Calendar },
  { id: 'invitations', label: 'Invitations', icon: Send },
  { id: 'guests', label: 'Guests', icon: Users },
  { id: 'giving', label: 'Giving', icon: DollarSign },
  { id: 'presentation', label: 'Presentation', icon: Presentation },
];

/** Keep the URL in step with the view so refresh/share lands in the same place. */
function syncUrl(view: View, eventId?: number | null, tab?: DetailTab) {
  try {
    const url = new URL(window.location.href);
    if (view === 'list') {
      url.searchParams.delete('event');
      url.searchParams.delete('view');
    } else {
      if (eventId) url.searchParams.set('event', String(eventId));
      url.searchParams.set('view', view === 'detail' ? tab || 'overview' : view);
    }
    window.history.replaceState({}, '', url.toString());
  } catch {
    /* URL sync is a convenience, never a hard failure */
  }
}

function readUrl(): { eventId: number | null; view: string | null } {
  try {
    const p = new URLSearchParams(window.location.search);
    const id = p.get('event');
    return { eventId: id ? Number(id) : null, view: p.get('view') };
  } catch {
    return { eventId: null, view: null };
  }
}

export const DonorEventsPage: React.FC = () => {
  const tabbed = useTabbedChrome();
  const [events, setEvents] = useState<DonorEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>('list');
  const [tab, setTab] = useState<DetailTab>('overview');
  const [current, setCurrent] = useState<DonorEvent | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [checkingIn, setCheckingIn] = useState(false);
  const [givingStation, setGivingStation] = useState(false);
  const [gifts, setGifts] = useState<Donation[]>([]);
  const [copied, setCopied] = useState(false);
  const [pipelineMsg, setPipelineMsg] = useState<string | null>(null);

  const loadList = useCallback(async () => {
    setError(null);
    try {
      setEvents(await listDonorEvents());
    } catch (e: any) {
      setError(e?.message || 'Failed to load donor events.');
      setEvents([]);
    }
  }, []);

  const loadEvent = useCallback(async (id: number) => {
    const ev = await getDonorEvent(id);
    setCurrent(ev);
    return ev;
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // Restore a deep link on first render.
  useEffect(() => {
    const { eventId, view: v } = readUrl();
    if (!eventId) return;
    loadEvent(eventId)
      .then(() => {
        setView('detail');
        const known = DETAIL_TABS.find((t) => t.id === v);
        setTab((known?.id as DetailTab) || 'overview');
      })
      .catch(() => syncUrl('list'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Gifts for the Giving tab, scoped to this event — possible only now that the
  // donations API projects and filters by event_id.
  useEffect(() => {
    if (view !== 'detail' || !current || tab !== 'giving') return;
    listDonations({ event_id: current.id, limit: 200 })
      .then(setGifts)
      .catch(() => setGifts([]));
  }, [view, current, tab]);

  const open = async (id: number, nextTab: DetailTab = 'overview') => {
    try {
      await loadEvent(id);
      setView('detail');
      setTab(nextTab);
      syncUrl('detail', id, nextTab);
    } catch (e: any) {
      setError(e?.message || 'Could not open that event.');
    }
  };

  const backToList = () => {
    setView('list');
    setCurrent(null);
    setPipelineMsg(null);
    syncUrl('list');
    loadList();
  };

  const submitForm = async (data: DonorEventCreate) => {
    setSaving(true);
    setFormError(null);
    try {
      if (view === 'edit' && current) {
        await updateDonorEvent(current.id, data);
        await loadEvent(current.id);
        setView('detail');
        syncUrl('detail', current.id, tab);
      } else {
        const created = await createDonorEvent(data);
        await loadList();
        await open(created.id, data.invite_cohort ? 'invitations' : 'overview');
      }
    } catch (e: any) {
      setFormError(e?.message || 'Could not save the event.');
    } finally {
      setSaving(false);
    }
  };

  const removeEvent = async (hard: boolean) => {
    if (!current) return;
    const label = hard ? 'permanently delete' : 'cancel';
    if (!window.confirm(`Are you sure you want to ${label} “${current.title}”?`)) return;
    try {
      await deleteDonorEvent(current.id, hard);
      backToList();
    } catch (e: any) {
      setError(e?.message || `Could not ${label} the event.`);
    }
  };

  const sendToPipeline = async () => {
    if (!current) return;
    setPipelineMsg(null);
    try {
      const r = await eventGuestsToPipeline(current.id, { attended_only: true, stage: 'cultivation' });
      setPipelineMsg(
        r.created > 0
          ? `Added ${r.created} donor${r.created === 1 ? '' : 's'} to the pipeline${r.skipped_existing ? ` (${r.skipped_existing} already there)` : ''}.`
          : 'Everyone who attended is already in the pipeline.',
      );
    } catch (e: any) {
      setPipelineMsg(e?.message || 'Could not add guests to the pipeline.');
    }
  };

  const copyLink = () => {
    if (!current) return;
    navigator.clipboard?.writeText(current.public_url || `${window.location.origin}/event/${current.id}`);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  const giftTotal = useMemo(
    () => gifts.filter((g) => g.status === 'succeeded').reduce((s, g) => s + Number(g.amount || 0), 0),
    [gifts],
  );

  // ---------------------------------------------------------------- form views
  if (view === 'new' || view === 'edit') {
    return (
      <DonorEventForm
        event={view === 'edit' ? current : null}
        saving={saving}
        error={formError}
        onCancel={() => {
          setFormError(null);
          if (view === 'edit' && current) {
            setView('detail');
            syncUrl('detail', current.id, tab);
          } else {
            backToList();
          }
        }}
        onSubmit={submitForm}
      />
    );
  }

  // ------------------------------------------------------------- detail view
  if (view === 'detail' && current) {
    if (checkingIn) {
      return (
        <EventCheckIn
          event={current}
          onExit={() => setCheckingIn(false)}
          onChanged={() => loadEvent(current.id)}
        />
      );
    }

    return (
      <div className="mx-auto max-w-6xl space-y-4 p-4 md:p-6 lg:p-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <button
              onClick={backToList}
              className="mt-0.5 rounded p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label="Back to donor events"
            >
              <ArrowLeft size={18} />
            </button>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="truncate text-xl font-semibold text-gray-900 dark:text-gray-100">{current.title}</h1>
                {current.status === 'cancelled' ? <Badge variant="destructive">Cancelled</Badge> : null}
                {current.cohort_name ? <Badge variant="outline">{current.cohort_name}</Badge> : null}
              </div>
              <p className="text-sm text-gray-500">
                {fmtRange(current.start_time, current.end_time)} ·{' '}
                {current.location_type === 'virtual' ? 'Virtual' : current.location_address || 'In person'}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={copyLink}>
              <Link2 size={14} className="mr-1" /> {copied ? 'Copied!' : 'Public link'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setCheckingIn(true)}>
              <CheckSquare size={14} className="mr-1" /> Check-in
            </Button>
            <Button size="sm" variant="outline" onClick={() => setView('edit')}>
              <Pencil size={14} className="mr-1" /> Edit
            </Button>
          </div>
        </div>

        {error ? (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">
            {error}
          </div>
        ) : null}

        <div className="flex gap-1 overflow-x-auto border-b border-gray-200 dark:border-gray-700">
          {DETAIL_TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => {
                  setTab(t.id);
                  syncUrl('detail', current.id, t.id);
                }}
                className={`flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium ${
                  tab === t.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
                }`}
              >
                <Icon size={15} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'overview' ? (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {[
                { label: 'Guest list', value: current.invited },
                { label: 'Notified', value: current.notified },
                { label: 'Accepted', value: current.accepted },
                { label: 'Attended', value: current.attended },
                { label: 'Raised', value: fmtMoney(current.raised), tone: 'text-green-700 dark:text-green-400' },
              ].map((s) => (
                <div key={s.label} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                  <div className={`text-xl font-semibold ${s.tone || 'text-gray-900 dark:text-gray-100'}`}>{s.value}</div>
                  <div className="text-xs text-gray-500">{s.label}</div>
                </div>
              ))}
            </div>

            {current.description ? (
              <div className="rounded-lg border border-gray-200 p-4 text-sm text-gray-700 dark:border-gray-700 dark:text-gray-300">
                {current.description}
              </div>
            ) : null}

            <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">After the event</h3>
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" onClick={sendToPipeline}>
                  <Target size={14} className="mr-1" /> Add attendees to the donor pipeline
                </Button>
                <Button size="sm" variant="outline" onClick={() => setTab('giving')}>
                  <DollarSign size={14} className="mr-1" /> Review gifts
                </Button>
              </div>
              {pipelineMsg ? <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">{pipelineMsg}</p> : null}
            </div>

            <div className="rounded-lg border border-red-100 p-4 dark:border-red-900/40">
              <h3 className="mb-1 text-sm font-semibold text-gray-900 dark:text-gray-100">Danger zone</h3>
              <p className="mb-2 text-xs text-gray-500">
                Cancelling keeps the guest list, attendance and gifts, and takes the public page down. Deleting is only
                possible while no gifts are attached.
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => removeEvent(false)}>
                  Cancel event
                </Button>
                <Button size="sm" variant="outline" onClick={() => removeEvent(true)}>
                  <Trash2 size={14} className="mr-1" /> Delete
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {tab === 'invitations' ? (
          <EventInvitations event={current} onChanged={() => loadEvent(current.id)} />
        ) : null}

        {tab === 'guests' ? (
          <div className="divide-y rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
            {(current.attendees || []).length === 0 ? (
              <div className="px-3 py-10 text-center text-sm text-gray-400">
                No guests yet — invite people from the Invitations tab.
              </div>
            ) : (
              (current.attendees || []).map((a) => (
                <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{a.name}</span>
                      <Badge variant={RSVP_VARIANT[a.response_status] || 'outline'}>{a.response_status}</Badge>
                      {a.attended ? <Badge variant="secondary">attended</Badge> : null}
                    </div>
                    <div className="mt-0.5 text-xs text-gray-400">
                      {a.email || 'No email'}
                      {a.invite_sent_at
                        ? ` · invited ${new Date(a.invite_sent_at).toLocaleDateString()} via ${a.invite_channels || 'email'}`
                        : ' · never notified'}
                      {a.invite_last_error ? ` · ${a.invite_last_error}` : ''}
                    </div>
                  </div>
                  {a.given ? (
                    <span className="shrink-0 text-sm font-semibold text-green-700 dark:text-green-400">
                      {fmtMoney(a.given)}
                    </span>
                  ) : null}
                </div>
              ))
            )}
          </div>
        ) : null}

        {tab === 'giving' ? (
          givingStation ? (
            <EventGivingStation
              event={current}
              onExit={() => setGivingStation(false)}
              onChanged={() => loadEvent(current.id)}
            />
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                <div>
                  <div className="text-2xl font-bold text-green-600">{fmtMoney(current.raised)}</div>
                  <div className="text-xs text-gray-500">
                    {current.gift_count} gift{current.gift_count === 1 ? '' : 's'} tagged to this event
                  </div>
                </div>
                <Button onClick={() => setGivingStation(true)}>
                  <DollarSign size={15} className="mr-1" /> Open giving station
                </Button>
              </div>
              <div className="divide-y rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
                {gifts.length === 0 ? (
                  <div className="px-3 py-10 text-center text-sm text-gray-400">
                    No gifts recorded at this event yet.
                  </div>
                ) : (
                  gifts.map((g) => (
                    <div key={g.id} className="flex items-center justify-between px-3 py-2.5">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                          {g.donor_display_name || g.donor_name || 'Anonymous donor'}
                        </div>
                        <div className="text-xs text-gray-400">
                          {g.method} · {new Date(g.created_at).toLocaleDateString()}
                          {g.receipt_state === 'sent' ? ' · receipt sent' : ''}
                          {g.receipt_state === 'none' ? ' · no receipt yet' : ''}
                        </div>
                      </div>
                      <span className="shrink-0 font-semibold text-gray-900 dark:text-gray-100">
                        {fmtMoney(Number(g.amount))}
                      </span>
                    </div>
                  ))
                )}
                {gifts.length > 0 ? (
                  <div className="flex justify-between px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    <span>Total</span>
                    <span>{fmtMoney(giftTotal)}</span>
                  </div>
                ) : null}
              </div>
            </div>
          )
        ) : null}

        {tab === 'presentation' ? (
          <EventPresentation
            eventId={current.id}
            materials={current.materials || []}
            onChange={() => loadEvent(current.id)}
          />
        ) : null}
      </div>
    );
  }

  // --------------------------------------------------------------- list view
  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 md:p-6 lg:p-8">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {!tabbed && <Calendar size={26} className="text-blue-600" />}
          <div>
            {!tabbed && <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Donor events</h1>}
            <p className="text-sm text-gray-500">Galas, dinners, and webinars — invitations, RSVP, and check-in.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadList}>
            <RefreshCw size={15} className="mr-1" /> Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setCurrent(null);
              setFormError(null);
              setView('new');
              syncUrl('new');
            }}
          >
            <Plus size={15} className="mr-1" /> New event
          </Button>
        </div>
      </header>

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      ) : null}

      {events === null ? (
        <div className="py-16 text-center text-gray-400">Loading donor events…</div>
      ) : events.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center dark:border-gray-700">
          <Calendar size={32} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-600 dark:text-gray-300">No donor events yet.</p>
          <p className="mt-1 text-sm text-gray-400">Create your first event to invite donors and track attendance.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {events.map((ev) => {
            const unnotified = Math.max(0, (ev.invited || 0) - (ev.notified || 0));
            return (
              <Card
                key={ev.id}
                className="cursor-pointer hover:shadow-md"
                onClick={() => open(ev.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => (e.key === 'Enter' ? open(ev.id) : undefined)}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center justify-between gap-2 text-base">
                    <span className="truncate">{ev.title}</span>
                    {ev.status === 'cancelled' ? <Badge variant="destructive">Cancelled</Badge> : null}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="text-sm text-gray-500">{fmtRange(ev.start_time, ev.end_time)}</div>
                  <div className="mt-1 flex items-center gap-1 text-xs text-gray-400">
                    <MapPin size={12} /> {ev.location_type === 'virtual' ? 'Virtual' : ev.location_address || 'In person'}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-600 dark:text-gray-300">
                    <span className="flex items-center gap-1">
                      <Users size={13} /> {ev.invited} invited
                    </span>
                    <span>{ev.accepted} accepted</span>
                    <span>{ev.attended} attended</span>
                  </div>
                  {unnotified > 0 ? (
                    <div className="mt-1 text-xs font-medium text-amber-600 dark:text-amber-400">
                      {unnotified} never notified
                    </div>
                  ) : null}
                  {ev.raised > 0 ? (
                    <div className="mt-1 flex items-center gap-1 text-xs font-semibold text-green-700 dark:text-green-400">
                      <DollarSign size={12} /> {fmtMoney(ev.raised)} raised
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DonorEventsPage;
